/**
 * What a row's hover strip should SAY, and which way its write should GO.
 *
 * Three of the strip's verbs are toggles, and all three used to render one-way:
 * Mark unread on an already-unread row, Snooze on a snoozed one, Archive on a
 * row sitting in Archived. Each fired the write it named, the write hit its own
 * transition guard in `services/mail_state.py` and changed nothing, and the
 * toast still reported success — so "I can't mark this unread again" and "I
 * can't unsnooze" (#626) were one defect wearing three labels.
 *
 * ## Why this is its own module
 *
 * It started inside `MailRow`, next to the icons it chooses. Every suite that
 * mocks `./MailRow` — `UnifiedInbox.test.jsx` does — then lost the function and
 * the surface threw `mailStateToggles is not defined` on the first click. A
 * pure decision shared by two surfaces and one component does not belong in any
 * of the three.
 *
 * The icon/label half (`actionDef`) and the direction half (`mailStateToggles`)
 * live together on purpose: if they were derived separately the button could
 * say "Snooze" while sending `until: null`.
 */
import {
    AlarmClockOff,
    Archive,
    ArchiveRestore,
    Clock,
    MailCheck,
    MailOpen,
    Reply,
    Trash2,
    Undo2,
    UserPlus,
} from 'lucide-react';

/**
 * The icon and title for one action, given the row it will act on.
 *
 * `folder` is the view the row is being DRAWN in, and it matters because a
 * pooled inbox's Trash is not a row flag: a message is in it by virtue of a
 * per-inbox removal record, so `mail.mail_folder` says nothing. Reading both
 * keeps one function correct for both lanes — the personal lane's rows carry
 * the flag, the unified lane's carry the view.
 */
export function actionDef(key, mail, folder) {
    const inTrash = mail?.mail_folder === 'trash' || folder === 'trash';
    switch (key) {
        case 'archive':
            return mail?.archived_at
                ? { icon: ArchiveRestore, title: 'Move to Inbox' }
                : { icon: Archive, title: 'Archive' };
        case 'markUnread':
            return mail?.is_read === false
                ? { icon: MailCheck, title: 'Mark read' }
                : { icon: MailOpen, title: 'Mark unread' };
        case 'snooze':
            return mail?.snoozed_until
                ? { icon: AlarmClockOff, title: 'Unsnooze' }
                : { icon: Clock, title: 'Snooze' };
        case 'delete':
            // In Trash the only remaining verb is the irreversible one, and it
            // must not wear the same word as the reversible move that put the
            // row there.
            return inTrash
                ? { icon: Trash2, title: 'Delete forever' }
                : { icon: Trash2, title: 'Move to Trash' };
        case 'restore':
            // The other door out of the bin. A Trash you can only empty is
            // not a Trash — it is a delay before the same data loss.
            return { icon: Undo2, title: 'Restore' };
        case 'reply':
            return { icon: Reply, title: 'Reply' };
        case 'assign':
            return { icon: UserPlus, title: 'Assign' };
        default:
            return null;
    }
}

/**
 * Which way each toggle should be written for this row.
 *
 * `unsnooze` is separate from a boolean because the two directions are not
 * symmetric: turning snooze OFF is `until: null`, but turning it ON needs a
 * preset instant that only the surface can resolve (in the VIEWER's display
 * zone — see the #578/#540 note in `useMailState`).
 */
export function mailStateToggles(mail) {
    return {
        starred: !(mail?.is_starred === true),
        important: !(mail?.is_important === true),
        read: mail?.is_read === false,
        archived: !mail?.archived_at,
        unsnooze: Boolean(mail?.snoozed_until),
        /** Trash is the one-way door; everywhere else Delete is a move. */
        purge: mail?.mail_folder === 'trash',
    };
}

/**
 * Whether a whole CONVERSATION is archived — judged on its INBOUND mail (#721).
 *
 * The thread reader acts on the conversation, not on one row, so its Archive
 * control has to know which way to write. Outbound messages are skipped because
 * the Gmail sync never archives them (`services/gmail_sync.py`, and the #706
 * migration has the same guard): a conversation archived in Gmail that holds a
 * reply you sent carries an outbound row with `archived_at: null` forever, so
 * "every message archived" would read false and the reader would offer Archive
 * on a conversation opened from Archived. A missing `direction` counts as
 * inbound — the reading `PersonalThreadView` already uses for its anchor
 * (`direction !== 'outbound'`).
 *
 * Why not anchor on one message the way the row strip reads its row: the two
 * readers anchor differently (the personal one on the seed row or the last
 * message, the unified one on the OLDEST — outbound whenever you started the
 * thread), so an anchor rule would disagree between the lanes on the same
 * conversation.
 *
 * `every` (over inbound), not `some`: a conversation that got a new inbound
 * reply after it was archived is partly back in the Inbox, and Archive — which
 * the server's transition guard applies only to messages not yet archived — is
 * the verb that finishes the job.
 *
 * A conversation with NO inbound message (sent-only) has nothing to defer to,
 * so its own messages decide. Without that fallback the reader could archive a
 * sent-only thread (the write lands on its outbound rows) and then never offer
 * the way back: it would read "not archived" and keep saying Archive.
 */
export function conversationArchived(messages) {
    const all = (Array.isArray(messages) ? messages : []).filter(Boolean);
    const inbound = all.filter((m) => m.direction !== 'outbound');
    const judged = inbound.length > 0 ? inbound : all;
    return judged.length > 0 && judged.every((m) => Boolean(m.archived_at));
}

/**
 * Whether any message of a conversation sits in Trash or Spam (`mail_folder`
 * set) — #721.
 *
 * The thread reader's Move to → Inbox clears `mail_folder` and nothing else. On
 * a conversation that is in a bin that is a real restore; on one that is not,
 * and is archived, it changes nothing (the verb for that is Move to Inbox on the
 * toolbar, which un-archives). The reader needs to tell the two apart.
 */
export function conversationBinned(messages) {
    return (Array.isArray(messages) ? messages : []).some((m) => Boolean(m && m.mail_folder));
}

/**
 * Does this action move the row OUT of the folder currently on screen?
 *
 * The list accumulates pages, so a refetch only ever replaces page 1. Archive
 * a message while scrolled to page 3 and the row stayed on screen: the server
 * had moved it, the refetch returned page 3 without it, and the accumulator —
 * which appends unseen rows and touches nothing else — had no reason to drop
 * it. That is the "sometimes it doesn't reflect properly" half of the rails
 * report, and it depends on how far the reader had scrolled, which is why it
 * looked intermittent.
 *
 * Knowing this lets the surface drop the row immediately, the way Delete
 * already did. Everything else (star in Inbox, read anywhere) leaves the row
 * in place and only needs its fields refreshed.
 *
 * `folder` is the rail's id, and the answers here mirror
 * `services/mail_folders.py::folder_clauses` — the one vocabulary both the
 * counts and the rows are built from. When they disagree the row either
 * lingers or vanishes early, so a change there is a change here.
 */
export function leavesFolderAfter(folder, key, toggles) {
    const view = folder || 'inbox';
    switch (key) {
        case 'archive':
            // Archiving leaves Inbox and every flag view that excludes
            // archived rows — `folder_clauses` gives Starred and Important the
            // same `archived_at: None`; un-archiving leaves the Archived view,
            // and Snoozed too: Move to Inbox ends a snooze (#721 — the server
            // wakes the row, as `set_archived` does for D3).
            return toggles.archived
                ? ['inbox', 'starred', 'important'].includes(view)
                : ['archived', 'snoozed'].includes(view);
        case 'snooze':
            // Snoozing leaves Inbox and Archived — both views exclude snoozed
            // rows (`folder_clauses`, #721 D2); waking leaves Snoozed.
            return toggles.unsnooze
                ? view === 'snoozed'
                : ['inbox', 'archived'].includes(view);
        case 'star':
            // Unstarring leaves Starred. Starring adds it there but does not
            // remove it from wherever it is now.
            return !toggles.starred && view === 'starred';
        case 'important':
            // Exactly Star's shape, for exactly Star's reason: the Important
            // view is a flag clause, so clearing the flag empties the row out
            // of it. #695's step 5 — "unmark from the Important view, it
            // leaves and the count drops".
            return !toggles.important && view === 'important';
        case 'spam':
            return view !== 'spam';
        case 'restore':
            // Restoring is the way OUT of Trash, so the row always leaves the
            // view it was clicked in.
            return true;
        case 'delete':
            return true;
        default:
            // Read/unread and assign never change a row's folder.
            return false;
    }
}
